import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  Alert,
  Button,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Spinner,
} from "reactstrap";
import { fetchVoidPreview, voidPayment } from "../../helpers/api/finance";
import handleErrorMessage from "../../helpers/errors/handleErrorMessage";
import { Toast } from "../UI/toast/Toast";

const TITLES = {
  bill: "Anular pago",
  advance: "Anular saldo a favor",
  provider: "Anular pago a proveedor",
  debt: "Anular cuenta por pagar",
};

const SUCCESS = {
  bill: "Pago anulado.",
  advance: "Saldo a favor anulado.",
  provider: "Pago a proveedor anulado.",
  debt: "Cuenta por pagar anulada.",
};

const PLACEHOLDERS = {
  bill: "Explica por qué se anula. Queda registrado y lo ve el propietario.",
  advance: "Explica por qué se anula el saldo a favor. Queda registrado y lo ve el propietario.",
  provider: "Explica por qué se anula. Queda registrado en el pago.",
  debt: "Explica por qué se anula. Queda registrado en la cuenta por pagar.",
};

const EFFECTS_TITLES = {
  debt: "Al anular:",
};

const NOTES = {
  debt: "La cuenta no se borra: queda como Anulada con este motivo.",
};

const MIN_REASON_LENGTH = 5;

const VoidPaymentModal = ({ isOpen, toggle, kind, paymentId, onVoided }) => {
  const [preview, setPreview] = useState(null);
  const [blockers, setBlockers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [reason, setReason] = useState("");

  useEffect(() => {
    if (!isOpen || !paymentId) {
      return undefined;
    }

    let isMounted = true;
    setPreview(null);
    setBlockers([]);
    setReason("");
    setIsLoading(true);

    fetchVoidPreview(kind, paymentId)
      .then((response) => {
        if (!isMounted) return;
        setPreview(response?.data || null);
        setBlockers(response?.data?.blockers || []);
      })
      .catch((error) => {
        if (isMounted) handleErrorMessage(error);
      })
      .finally(() => {
        if (isMounted) setIsLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [isOpen, kind, paymentId]);

  const trimmedReason = reason.trim();
  const canSubmit =
    Boolean(preview?.can_void) &&
    blockers.length === 0 &&
    trimmedReason.length >= MIN_REASON_LENGTH &&
    !isSubmitting;

  const close = () => {
    if (!isSubmitting) toggle();
  };

  const submit = async () => {
    if (!canSubmit) return;
    setIsSubmitting(true);

    try {
      await voidPayment(kind, paymentId, trimmedReason);
      Toast.fire({ type: "success", title: SUCCESS[kind] });
      toggle();
      if (onVoided) onVoided();
    } catch (error) {
      const serverBlockers = error?.data?.blockers;
      if (Array.isArray(serverBlockers) && serverBlockers.length > 0) {
        setBlockers(serverBlockers);
      } else {
        handleErrorMessage(error);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={close} centered>
      <ModalHeader toggle={close}>{TITLES[kind]}</ModalHeader>
      <ModalBody>
        {isLoading ? (
          <div className="text-center py-3">
            <Spinner color="primary" />
          </div>
        ) : (
          <>
            {blockers.length > 0 && (
              <Alert color="danger" className="fs--1">
                <p className="mb-1 font-weight-semi-bold">No se puede anular:</p>
                <ul className="mb-0 pl-3">
                  {blockers.map((blocker, index) => (
                    <li key={index}>{blocker}</li>
                  ))}
                </ul>
              </Alert>
            )}
            {preview?.effects?.length > 0 && (
              <>
                <p className="fs--1 mb-1 font-weight-semi-bold">
                  {EFFECTS_TITLES[kind] || "Al anular se registra el movimiento inverso:"}
                </p>
                <ul className="fs--1 pl-3">
                  {preview.effects.map((effect, index) => (
                    <li key={index}>{effect}</li>
                  ))}
                </ul>
              </>
            )}
            <FormGroup className="mb-0">
              <Label for="void-reason">Motivo de la anulación *</Label>
              <Input
                id="void-reason"
                type="textarea"
                rows={3}
                maxLength={1000}
                value={reason}
                disabled={isSubmitting || blockers.length > 0}
                placeholder={PLACEHOLDERS[kind]}
                onChange={(event) => setReason(event.target.value)}
              />
              <small className="text-600">
                {NOTES[kind] || "El pago no se borra: queda como Anulado con este motivo."}
              </small>
            </FormGroup>
          </>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="falcon-default" onClick={close} disabled={isSubmitting}>
          Volver
        </Button>
        <Button color="danger" onClick={submit} disabled={!canSubmit}>
          {isSubmitting ? <Spinner size="sm" /> : TITLES[kind]}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

VoidPaymentModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  toggle: PropTypes.func.isRequired,
  kind: PropTypes.oneOf(["bill", "advance", "provider", "debt"]).isRequired,
  paymentId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onVoided: PropTypes.func,
};

export default VoidPaymentModal;
